<template>
  <div class="w-full flex flex-row justify-center user-location-main-container">
    <div
      class="w-full max-w-1440 mx-5 location-inner-container flex justify-center items-start user-location-card-img"
      :style="{ backgroundImage: `url(${currentImage})`, opacity: imageOpacity }"
    >
      <div class="hidden lg:inline-block">
        <div class="mt-12 flex flex-row justify-between items-center gap-1 shadow-2xl">
          <div class="input-container flex flex-row justify-around items-center">
            <div class="flex items-center gap-1">
              <geolocate @success="searchByCoordinates" :showIcon="false" :onlyIcon="true" />
              <img v-if="loading" class="animate-spin h-4" src="@/images/spinner.svg" alt="Loading">
            </div>
            <input
              @keyup.enter="search"
              v-model="query"
              @click="coordinates = null"
              :class="{ 'is-danger': error }"
              class="bg-transparent w-full focus:ring-0 focus:outline-none"
              type="search"
              placeholder="City or Zipcode"
            />
            <toggle @toggleValue="setToggleOption" />
          </div>
          <div>
            <button @click="search" class="location-search-btn flex justify-center items-center">
              <img class="" src="@/images/user-location/search.svg" alt="Search icon">
            </button>
          </div>
        </div>
        <div>
          <div v-if="error" class="help py-1 text-center bg-white text-red-500 border rounded-md">
            {{ error }}
            <b><a @click.prevent="resetSearch()">Try Again</a></b>
          </div>
          <div v-if="cities.length" role="menu" class="mt-1 border rounded-md">
            <div class="dropdown-content modal-card-body" style="max-height: 300px !important;">
              <p class="dropdown-item"><b>We found mulitple results...</b></p>
              <a v-for="city in cities" :key="city.id" :href="cityPath(city.node)" class="dropdown-item">
                {{ city.node.name }}, {{ city.node.stateId | upcase }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import bgImg01 from '@/images/user-location/bg-01.png'
import bgImg02 from '@/images/user-location/bg-02.png'
import bgImg03 from '@/images/user-location/bg-03.jpeg'

import ahoy from 'shared/ahoy'
import Toggle from './helpers/toggle.vue'
import Geolocate from '../location-search/geolocate.vue'
import { zipQuery, citySearchQuery, geoSearchQuery, solutionByCity } from 'shared/graphql/queries'
import { graphqlQuery } from 'shared/graphql'
import { cityPath } from '../../shared/city-helper'
import { mapState } from 'vuex'
const ZIP_REGEX = /^\d{5}$/
const redirectToCity = (city) => window.location.href = cityPath(city)

export default {
  components: {
    Toggle,
    Geolocate
  },
  data() {
    return {
      currentImageIndex: 0,
      images: [bgImg01, bgImg02, bgImg03],
      imageOpacity: 1,
      
      loading: 0,
      solutionSlug: '',
      error: null,
      query: '',
      coordinates: null,
      cities: []
    }
  },

  filters: {
    upcase(string) {
      return string.toUpperCase()
    }
  },
  computed: {
    ...mapState('shared', ['zipcode']),
    ...mapState('quote', ['lead']),
    isButtonDisabled() {
      // 5 digits, or at least 3 characters
      return !/^(\d{5})|(\D{3,})$/i.test(this.query)
    },
    currentImage() {
      return this.images[this.currentImageIndex]
    }
  },
  watch: {
    query() {
      if (this.query === '') {
        this.error = null
        this.cities = []
      } else if (this.error && this.query !== this.lastQuery) {
        this.error = null
      }
    }
  },
  mounted() {    
    this.startImageRotation()
    this.loading = 0
    this.query = this.zipcode
  },
  methods: {
    startImageRotation() {
      setInterval(() => {
        this.fadeOutImage(() => {
          this.currentImageIndex = (this.currentImageIndex + 1) % this.images.length
          this.fadeInImage()
        })
      }, 20000) // Change every 20 seconds
    },
    fadeOutImage(callback) {
      this.imageOpacity = 0.9
      setTimeout(() => {
        callback()
      }, 1500) // Match this duration with the CSS transition
    },
    fadeInImage() {
      setTimeout(() => {
        this.imageOpacity = 1
      }, 1) // Small delay to trigger transition
    },
    setToggleOption(value) {          
      this.solutionSlug = value
    },

    cityPath,
    handleClose() {
      this.$el.parentNode.removeChild(this.$el)
      this.$emit('close')
    },
    searchByCoordinates(coordinates) {
      this.coordinates = coordinates
      this.query = ''
      this.search()
    },
    resetSearch() {
      this.query = ''
      this.error = null
    },
    getRequestBody() {
      if (ZIP_REGEX.test(this.query)) {
        return {
          query: zipQuery,
          variables: {
            id: this.query
          }
        }
      } else if (this.coordinates) {
        return {
          query: geoSearchQuery,
          variables: {
            coordinates: this.coordinates
          }
        }
      } else {
        return {
          query: citySearchQuery,
          variables: {
            query: this.query
          }
        }
      }
    },
    async trackSearch() {
      ahoy.track('location.search', {
        coordinates: this.coordinates,
        query: this.query
      })
    },
    async search() {
      this.cities = []
      this.loading++
      this.lastQuery = this.query = this.query.trim()
      try {
        this.trackSearch()
        const result = await graphqlQuery(this.getRequestBody())
        if (result.data.zipcode) {
          const aboutSlug = `/service-areas/${result.data.zipcode.city.id}/`
          localStorage.setItem('serviceAreaAboutSlug', JSON.stringify(aboutSlug)) 
        } 
        return this.handleResult(result.data || {})
      } catch (e) {
        // handle error
        console.warn(e)
        this.error = 'There was an error while searching.'
        this.loading = 0
      }
    },
    async handleResult(result) {
      let { zipcode, zipcodes, cities } = result
      if (!zipcode && zipcodes && zipcodes.edges.length === 1) {
        zipcode = zipcodes.edges[0].node
      }
      if (this.solutionSlug && zipcode) {
        const data = await this.getSolutionPage(zipcode)
        if (data && data.solutionByCity && data.solutionByCity.solution) {
          if (this.lead) {
            await this.$store.dispatch('quote/lead.zipcode.update', {lead: this.lead, zipcode: zipcode.id})
          } else {
            this.$store.commit('shared/zipcode.set', zipcode.id)
          }
          this.$emit('close')
          this.loading = 0
          window.location.href = `/partners/${data.solutionByCity.organization.id}/${data.solutionByCity.solution.slug}`
          return
        }
      }
      if (zipcode) {
        if (this.lead) {
          await this.$store.dispatch('quote/lead.zipcode.update', {lead: this.lead, zipcode: zipcode.id})
        } else {
          this.$store.commit('shared/zipcode.set', zipcode.id)
        }
        this.$emit('close')
        return redirectToCity(zipcode.city)
      } else if (cities && cities.edges.length) {
        if (cities.edges.length === 1) {
          this.$emit('close')
          return redirectToCity(cities.edges[0].node)
        } else {
          this.cities = cities.edges
        }
      } else {
        this.error = 'Sorry, we couldn\'t find that location.'
      }
      this.loading = 0
    },
    async getSolutionPage(zipcode) {
      const body = {
        query: solutionByCity,
        variables: {
          cityId: zipcode.city.id,
          solutionSlug: this.solutionSlug
        }
      }
      const result = await graphqlQuery(body)
      return result.data
    }
  }
}
</script>

<style scoped>
.user-location-main-container {
  padding-top: 48px;
  background: linear-gradient(to bottom, #FBF9F6 50%, #0C0E20 50%);
}
.user-location-card-img {
  background-color: #FBF9F6 !important;
}
.location-inner-container {
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  width: 100%;
  height: 750px;

  background-color: #0000000D;
  border: 1px transparent;
  border-radius: 12px;

  transition: opacity 1.5s ease-in-out; /* Fade transition effect */
}
.input-container {
  width: 568px;
  height: 44px;
  padding: 18px 10px 18px 10px;
  gap: 10px;
  border-radius: 8px;
  border: 1px transparent;
  background-color: #FBF9F6;
}
.drop-shadow {
  --tw-drop-shadow: drop-shadow(0 4px 3px rgba(77, 132, 255, 0.07)) drop-shadow(0 2px 2px rgba(77, 132, 255, 0.06));
}
.location-search-btn {
  width: 44px;
  height: 44px;
  border: 1px transparent;
  border-radius: 8px;
  background-color: #4D84FF;
}
@media(max-width: 1024px) {
  .user-location-main-container {
    padding-top: 24px
  }
  .location-inner-container {
    width: 100%;
    height: 200px;
  }
}
</style>
